/**
 * @file
 * Market Facet Enhancement (BEF backend).
 *
 * The market filter is rendered by BEF as a flat checkbox list. This behavior
 * rebuilds the legacy hierarchical design client-side using the parent → child
 * map provided in drupalSettings.teideMarketFacet.groups:
 * - Parent group headers (Nacional / Internacional)
 * - Parent toggle relabel (Total <group>)
 * - Indeterminate / active parent state derived from the children
 * - Visual state on children when the parent is active
 * - Internal search box (filter by label)
 * - Show more / show less per group
 *
 * The underlying form inputs (name / value / checked) are never modified, so
 * selections still submit normally through the "Mostrar resultados" button.
 */

(function (Drupal, drupalSettings, once) {
  'use strict';

  const INITIAL_VISIBLE_CHILDREN = 5;
  const OVERFLOW_CLASS = 'market-facet-overflow-hidden';
  const SEARCH_HIDDEN_CLASS = 'market-facet-search-hidden';

  Drupal.behaviors.marketFacetEnhance = {
    attach() {
      const groups =
        (drupalSettings.teideMarketFacet &&
          drupalSettings.teideMarketFacet.groups) ||
        [];

      if (!groups.length) {
        return;
      }

      once('market-facet-enhanced', '.market-facet-widget').forEach((widget) => {
        enhanceMarketFacet(widget, groups);
      });
    },
  };

  function enhanceMarketFacet(widget, groups) {
    const list = widget.querySelector('.js-market-facet-list');
    if (!list) {
      return;
    }

    // Index each top-level facet item <li> by its checkbox value (term id).
    const liByTid = new Map();
    list.querySelectorAll(':scope > li.facet-item').forEach((li) => {
      const checkbox = li.querySelector('input[type="checkbox"]');
      if (checkbox) {
        liByTid.set(String(checkbox.value), li);
      }
    });

    groups.forEach((group) => {
      const parentLi = liByTid.get(String(group.parent));
      if (parentLi) {
        buildGroup(parentLi, group, liByTid);
      }
    });

    // Re-append groups in declared order (children are now nested inside).
    groups.forEach((group) => {
      const parentLi = liByTid.get(String(group.parent));
      if (parentLi) {
        list.appendChild(parentLi);
      }
    });

    setupOverflow(widget);
    setupSearch(widget, list);
  }

  function buildGroup(parentLi, group, liByTid) {
    parentLi.classList.add('market-facet-group');

    // Section header (Nacional / Internacional).
    const header = document.createElement('div');
    header.className = 'market-facet-group__header';
    header.textContent = group.label;

    // Wrap the parent checkbox row in the toggle container and relabel it.
    const formItem = parentLi.querySelector('.form-item') || parentLi;
    const toggle = document.createElement('div');
    toggle.className = 'market-facet-group__toggle';

    const parentLabel = formItem.querySelector('label');
    if (parentLabel) {
      parentLabel.textContent = `${Drupal.t('Total')} ${group.label}`;
    }

    parentLi.insertBefore(header, parentLi.firstChild);
    parentLi.insertBefore(toggle, header.nextSibling);
    toggle.appendChild(formItem);

    // Move the children into a nested list.
    const childList = document.createElement('ul');
    childList.className = 'item-list__checkbox';
    group.children.forEach((tid) => {
      const childLi = liByTid.get(String(tid));
      if (childLi) {
        childList.appendChild(childLi);
      }
    });
    parentLi.appendChild(childList);

    // Keep the parent state (active / indeterminate) in sync with the children.
    const parentCheckbox = toggle.querySelector('input[type="checkbox"]');
    const childCheckboxes = Array.from(
      childList.querySelectorAll('input[type="checkbox"]'),
    );

    const sync = () => updateGroupState(parentLi, parentCheckbox, childCheckboxes);
    sync();

    if (parentCheckbox) {
      parentCheckbox.addEventListener('change', sync);
    }
    childCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', sync);
    });
  }

  function updateGroupState(parentLi, parentCheckbox, childCheckboxes) {
    const isChecked = parentCheckbox ? parentCheckbox.checked : false;
    const anyChildChecked = childCheckboxes.some((checkbox) => checkbox.checked);
    const isPartial = anyChildChecked && !isChecked;

    if (parentCheckbox) {
      parentCheckbox.indeterminate = isPartial;
    }

    parentLi.classList.toggle('market-facet-group--parent-active', isChecked);
    parentLi.classList.toggle('market-facet-group--parent-partial', isPartial);

    parentLi
      .querySelectorAll(':scope > ul > .facet-item')
      .forEach((childItem) => {
        const checkbox = childItem.querySelector('input[type="checkbox"]');
        const childActive = checkbox ? checkbox.checked : false;
        childItem.classList.toggle(
          'market-facet-child--included',
          isChecked && !childActive,
        );
      });
  }

  function setupOverflow(widget) {
    widget.querySelectorAll('.market-facet-group').forEach((group) => {
      const childList = group.querySelector(':scope > ul');
      if (!childList) {
        return;
      }

      const children = Array.from(
        childList.querySelectorAll(':scope > .facet-item'),
      );
      const overflow = children.slice(INITIAL_VISIBLE_CHILDREN);
      if (!overflow.length) {
        return;
      }

      overflow.forEach((item) => {
        item.dataset.marketOverflow = 'true';
      });
      collapseOverflow(group);

      const link = document.createElement('a');
      link.href = '#';
      link.className = 'market-facet-show-more';
      link.textContent = Drupal.t('Mostrar más');
      link.setAttribute('aria-expanded', 'false');
      childList.insertAdjacentElement('afterend', link);

      link.addEventListener('click', (event) => {
        event.preventDefault();
        const expanded = group.classList.toggle('market-facet-expanded');
        if (expanded) {
          expandOverflow(group);
        } else {
          collapseOverflow(group);
        }
        link.textContent = expanded
          ? Drupal.t('Mostrar menos')
          : Drupal.t('Mostrar más');
        link.setAttribute('aria-expanded', String(expanded));
      });
    });
  }

  function collapseOverflow(group) {
    group
      .querySelectorAll('[data-market-overflow="true"]')
      .forEach((item) => {
        const checkbox = item.querySelector('input[type="checkbox"]');
        const active = checkbox ? checkbox.checked : false;
        item.classList.toggle(OVERFLOW_CLASS, !active);
      });
  }

  function expandOverflow(group) {
    group
      .querySelectorAll('[data-market-overflow="true"]')
      .forEach((item) => item.classList.remove(OVERFLOW_CLASS));
  }

  function setupSearch(widget, list) {
    const input = widget.querySelector('input.facets-widget-searchbox');
    const noResult = widget.querySelector('.facets-widget-searchbox-no-result');
    if (!input) {
      return;
    }

    input.addEventListener('input', () => {
      const query = input.value.trim().toLowerCase();
      const searching = query.length > 0;
      widget.classList.toggle('market-facet-searching', searching);

      let anyVisible = false;

      widget.querySelectorAll('.market-facet-group').forEach((group) => {
        let groupHasMatch = false;

        group
          .querySelectorAll(':scope > ul > .facet-item')
          .forEach((item) => {
            const label = item.querySelector('label');
            const text = label ? label.textContent.toLowerCase() : '';
            const match = !searching || text.indexOf(query) !== -1;
            item.classList.toggle(SEARCH_HIDDEN_CLASS, searching && !match);
            if (match) {
              groupHasMatch = true;
            }
          });

        const parentLabel = group.querySelector(
          '.market-facet-group__toggle label',
        );
        const parentText = parentLabel
          ? parentLabel.textContent.toLowerCase()
          : '';
        const parentMatch = !searching || parentText.indexOf(query) !== -1;
        const showGroup = !searching || groupHasMatch || parentMatch;

        group.classList.toggle(SEARCH_HIDDEN_CLASS, !showGroup);
        if (showGroup) {
          anyVisible = true;
        }

        if (searching) {
          // Reveal every (matching) child while searching.
          group
            .querySelectorAll('[data-market-overflow="true"]')
            .forEach((item) => item.classList.remove(OVERFLOW_CLASS));
        } else if (!group.classList.contains('market-facet-expanded')) {
          collapseOverflow(group);
        }
      });

      if (noResult) {
        noResult.hidden = anyVisible;
      }
    });
  }
})(Drupal, drupalSettings, once);
